import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SPECIALTY_COLORS = {
  'GP (General Practitioner)': 'bg-blue-100 text-blue-700',
  'Neurologist': 'bg-purple-100 text-purple-700',
  'Cardiologist': 'bg-red-100 text-red-700',
  'Dermatologist': 'bg-yellow-100 text-yellow-700',
  'Orthopaedic Surgeon': 'bg-orange-100 text-orange-700',
  'Gastroenterologist': 'bg-green-100 text-green-700',
  'Endocrinologist': 'bg-teal-100 text-teal-700',
  'Rheumatologist': 'bg-indigo-100 text-indigo-700',
  'Pulmonologist': 'bg-cyan-100 text-cyan-700',
  'Psychiatrist': 'bg-pink-100 text-pink-700',
};

const getCountdownInfo = (appointmentDate) => {
  const now = new Date();
  const appt = new Date(appointmentDate);
  const diffMs = appt?.getTime() - now?.getTime();
  const diffHours = diffMs / (1000 * 60 * 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffHours < 0) return { label: 'Past', color: 'text-muted-foreground', urgent: false };
  if (diffHours < 2) return { label: 'In < 2 hours', color: 'text-error font-semibold', urgent: true };
  if (diffHours < 24) return { label: `In ${Math.round(diffHours)}h`, color: 'text-warning font-semibold', urgent: true };
  if (diffDays === 1) return { label: 'Tomorrow', color: 'text-warning font-semibold', urgent: true };
  return { label: `In ${diffDays} days`, color: 'text-success', urgent: false };
};

const AppointmentCard = ({ appointment, onEdit, onDelete }) => {
  const navigate = useNavigate();
  const apptDate = new Date(appointment?.appointment_date);
  const countdown = getCountdownInfo(appointment?.appointment_date);
  const specialtyColor = SPECIALTY_COLORS?.[appointment?.specialty] || 'bg-muted text-muted-foreground';

  const formattedDate = apptDate?.toLocaleDateString('en-GB', {
    weekday: 'short', day: 'numeric', month: 'short', year: 'numeric'
  });
  const formattedTime = apptDate?.toLocaleTimeString('en-GB', {
    hour: 'numeric', minute: '2-digit', hour12: true
  });

  const handlePrepareReport = () => {
    const apptDateObj = new Date(appointment?.appointment_date);
    const startDate = new Date(apptDateObj);
    startDate?.setDate(startDate?.getDate() - 30);
    navigate('/prepare-for-doctor-visit', {
      state: {
        fromAppointment: true,
        appointmentId: appointment?.id,
        doctorName: appointment?.doctor_name,
        specialty: appointment?.specialty,
        appointmentDate: appointment?.appointment_date,
        dateRangeStart: startDate?.toISOString(),
        dateRangeEnd: appointment?.appointment_date
      }
    });
  };

  return (
    <div className={`bg-card border rounded-xl p-4 shadow-sm transition-all hover:shadow-md ${countdown?.urgent ? 'border-warning/40' : 'border-border'}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Icon name="Stethoscope" size={20} className="text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-foreground text-sm truncate">{appointment?.doctor_name}</h3>
            {appointment?.specialty && (
              <span className={`inline-block text-xs px-2 py-0.5 rounded-full font-medium mt-0.5 ${specialtyColor}`}>
                {appointment?.specialty}
              </span>
            )}
          </div>
        </div>
        <div className={`text-xs flex-shrink-0 ${countdown?.color}`}>{countdown?.label}</div>
      </div>
      <div className="space-y-1.5 mb-3">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Icon name="Calendar" size={13} className="flex-shrink-0" />
          <span>{formattedDate} at {formattedTime}</span>
        </div>
        {appointment?.location && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Icon name="MapPin" size={13} className="flex-shrink-0" />
            <span className="truncate">{appointment?.location}</span>
          </div>
        )}
        {appointment?.notes && (
          <div className="flex items-start gap-2 text-xs text-muted-foreground">
            <Icon name="FileText" size={13} className="flex-shrink-0 mt-0.5" />
            <span className="line-clamp-2">{appointment?.notes}</span>
          </div>
        )}
      </div>
      <div className="flex items-center gap-1.5 mb-3">
        <div className={`w-2 h-2 rounded-full ${appointment?.reminder_7day_sent ? 'bg-success' : 'bg-muted'}`} title="7-day reminder" />
        <div className={`w-2 h-2 rounded-full ${appointment?.reminder_1day_sent ? 'bg-success' : 'bg-muted'}`} title="1-day reminder" />
        <div className={`w-2 h-2 rounded-full ${appointment?.reminder_2hour_sent ? 'bg-success' : 'bg-muted'}`} title="2-hour reminder" />
        <span className="text-xs text-muted-foreground ml-1">Reminders</span>
      </div>
      <div className="flex gap-2">
        <Button
          variant="default"
          size="sm"
          onClick={handlePrepareReport}
          iconName="ClipboardList"
          iconPosition="left"
          className="flex-1 text-xs"
        >
          Prepare Report
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onEdit(appointment)}
          iconName="Pencil"
          className="px-2.5"
          aria-label="Edit appointment"
        />
        <Button
          variant="outline"
          size="sm"
          onClick={() => onDelete(appointment?.id)}
          iconName="Trash2"
          className="px-2.5 text-error border-error/30 hover:bg-error/10"
          aria-label="Delete appointment"
        />
      </div>
    </div>
  );
};

export default AppointmentCard;
